'use client';

import { fetchLiveblogItemsAction } from '@/actions/fetchLiveblogItemsAction';
import type { WrittenContent } from '@/types/content';
import { useCallback, useEffect, useRef, useState } from 'react';
import LiveblogUpdate from '../LiveblogUpdate/LiveblogUpdate';
import { hasTimeExceeded } from '@/utils/timeUtils';
import FloatingButton from '@/components/ui/Button/FloatingButton/FloatingButton';
import { useDictionary } from '@/context/DictionaryContext';
import { useScrollPagination } from '@/hooks/useScrollPagination';

import styles from './LiveblogTimeline.module.scss';


interface LiveBlogTimelineProps {
  liveblogId: string;
  initialUpdates: WrittenContent[];
}

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 120_000;

const mergeUniqueUpdates = (
  updates: WrittenContent[],
  newUpdates: WrittenContent[],
  prepend: boolean = false,
) => {
  const existingIds = new Set(updates.map(item => item.id));
  const uniqueUpdates = newUpdates.filter(item => !existingIds.has(item.id));
  return prepend ? [...uniqueUpdates, ...updates] : [...updates, ...uniqueUpdates];
};

export const LiveblogTimeline = ({ liveblogId, initialUpdates }: LiveBlogTimelineProps) => {
  const [updates, setUpdates] = useState<WrittenContent[]>(initialUpdates);
  const [page, setPage] = useState<number>(1);
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState<boolean>(true);
  const [pendingUpdates, setPendingUpdates] = useState<WrittenContent[]>([]);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const { t } = useDictionary();

  const updatesRef = useRef(updates);
  useEffect(() => { updatesRef.current = updates; }, [updates]);

  useEffect(() => {
    console.log('errorMessage', errorMessage);
  }, [errorMessage]);

  const fetchNextPage = useCallback(async () => {
    if (!hasMorePages || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const nextPage = page + 1;

      const newUpdates = await fetchLiveblogItemsAction({
        liveblogId,
        page: nextPage,
        pageSize: 10,
      }) ?? [];

      setUpdates(prevUpdates => {
        const existingIds = new Set(prevUpdates.map(item => item.id));
        const uniqueUpdates = newUpdates.filter(item => !existingIds.has(item.id));

        if (initialUpdates.length === 0) return prevUpdates;

        setPage(nextPage);
        return [...prevUpdates, ...uniqueUpdates];
      });

      if (newUpdates.length === 0) setHasMorePages(false);

    } catch (error) {
      setErrorMessage('Failed to load more updates');
    } finally {
      setIsLoading(false);
    }
  }, [liveblogId, page, hasMorePages, isLoading]);

  useScrollPagination({
    targetRef: loaderRef,
    onLoadMore: fetchNextPage,
    hasMore: hasMorePages,
    isLoading,
  });

  const isLiveblogActive = hasTimeExceeded(updatesRef.current[0]?.publishedAt, '5 hours') === false;

  const chekcForNewUpdates = useCallback(async () => {
    if (!isLiveblogActive) return;
    try {
      const newUpdates = await fetchLiveblogItemsAction({
        liveblogId,
        page: 1,
        pageSize: 5,
      }) || [];

      if (Array.isArray(newUpdates) && newUpdates?.length > 0) {
        const currentIds = new Set(updatesRef.current.map(item => item.id));
        const incomingUpdates = newUpdates.filter(item => {
          if (updatesRef.current.length === 0) return true;
          return (
            !currentIds.has(item.id) &&
            item.publishedAt >= updatesRef.current[0].publishedAt
          );
        });

        if (incomingUpdates?.length > 0) {
          if (autoUpdateEnabled) {
            setUpdates(prev => [...incomingUpdates, ...prev]);
          } else {
            setPendingUpdates(prev => {
              const pendingIds = new Set(prev.map(item => item.id));
              const mergedPending = [
                ...prev,
                ...incomingUpdates.filter(item => !pendingIds.has(item.id)),
              ];
              return mergedPending;
            });
          }
        }
      }
    } catch (error) {
      setErrorMessage('Failed to load new updates');
    }
  }, [liveblogId, autoUpdateEnabled, pendingUpdates]);

  useEffect(() => {
    const interval = setInterval(() => chekcForNewUpdates(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [chekcForNewUpdates]);

  const mergePendingUpdates = () => [...pendingUpdates].reverse();

  const handleManualUpdate = () => {
    setUpdates(prev => [...mergePendingUpdates(), ...prev]);
    setPendingUpdates([]);
  };

  const handleToggleAutoUpdate = () => {
    setAutoUpdateEnabled(prev => {
      const newAutoUpdate = !prev;
      if (newAutoUpdate && pendingUpdates?.length > 0) {
        setUpdates(prevUpdates => [...mergePendingUpdates(), ...prevUpdates]);
        setPendingUpdates([]);
      }
      return newAutoUpdate;
    });
  };

  //const { isScrolled, scrollPosition } = useScroll({ threshold: 400 });
  //useEffect(() => setAutoUpdateEnabled(!isScrolled), [isScrolled]);

  const shouldShowLoader = updates.length >= 10;

  const Loader = () => {
    if (!shouldShowLoader) return null;

    if (hasMorePages) {
      return (
        <FloatingButton
          label={t('loadMore')}
          loadingText={t('loading')}
          icon={''}
          loadingIcon={''}
          isLoading={isLoading}
          onClick={fetchNextPage}
        />
      );
    }

    return <span className={styles.noMoreContent}>{t('noMoreContent')}</span>;
  };

  return (
    <>
      <main className={styles.liveblogTimeline}>
        {updates.length === 0 ? (
          <p className={styles.noContent}>{t('noContent')}</p>
        ) : (
          <section className={styles.liveblogTimeline__updates}>
            {updates.map(update => (
              <LiveblogUpdate key={update.id} update={update} />
            ))}
          </section>
        )}
      </main>
      <div className={styles.liveblogTimeline__loader}>
        <div ref={loaderRef}>
          <Loader />
        </div>
      </div>
    </>
  );
};

export default LiveblogTimeline;